import * as React from 'react';
import { useState, useCallback } from 'react';

/** Redux Packages. */
import { useDispatch } from 'react-redux';

/** Material UI Packages. */
import { Autocomplete, Box, Chip, FormControl, MenuItem, Popper, Select, TextField, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

/** Material UI Icons. */
import CancelIcon from '@mui/icons-material/Cancel';

/** Internal Components. */
import InputField from 'shared/ui/InputForm';
import { partner_type } from 'shared/config/constantsData';
import { readSocketData } from 'shared/lib/fetchSocket';

const StyledPopper = styled(Popper)({
    '& .MuiAutocomplete-listbox': {
        fontSize: 12,
        overflowY: 'auto',
    },
    overflowY: 'auto',
});

/** Component for single select type input. */
const RequiredInfoSelectField = (props) => {
    /** Get props from NewTradePartnerDialog.js and EditTradePartnerDialog.js. */
    const { inputObj, onChange, state, purpose, partnerTypeOptions, validator } = props;
    let options = null;
    let selectFieldHandler = () => {};
    /**  Provide option values based on the field of `inputObj` and its purpose.
     *   Display a confirmation dialog to notify the user if the `Client` value (a value of "partner_type") is about to change.
     */
    if (purpose === 'new') {
        options = Array.isArray(partnerTypeOptions) ? partnerTypeOptions : [];
        selectFieldHandler = onChange;
    } else if (purpose === 'edit') {
        if (inputObj.field === 'partner_type' && Array.isArray(partnerTypeOptions) && partnerTypeOptions.length > 0) {
            const current = state[inputObj.field];
            const base = [...partnerTypeOptions];
            if (current && !base.some((item) => item.content === current)) {
                options = [{ id: 0, content: current }, ...base];
            } else {
                options = base;
            }
        } else {
            options = partner_type;
        }
        selectFieldHandler = (event, newValue) => {
            if (
                state.partner_type === 'Client' &&
                !window.confirm(
                    `Changing the type from "Client" to another type may disable certain features. Please confirm if you want to proceed with this change.`
                )
            )
                return;
            onChange(event, newValue);
        };
    }

    return (
        <>
            <FormControl
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '25px',
                }}
                error={inputObj.required ? validator[inputObj.field] : false}
            >
                <Typography
                    sx={{
                        display: 'inline',
                        color: '#003a52',
                        fontSize: '13px',
                        fontWeight: 500,
                        width: '100px',
                    }}
                >
                    {inputObj.name}
                </Typography>
                <Select
                    name={inputObj.field}
                    value={
                        state[inputObj.field] && options.some((item) => item.content === state[inputObj.field])
                            ? state[inputObj.field]
                            : options[0]?.content ?? ''
                    }
                    onChange={selectFieldHandler}
                    sx={{
                        fontSize: '13px',
                        '& .MuiSelect-select': {
                            p: 1,
                        },
                        height: '27px',
                        overflow: 'hidden',
                        flex: 1,
                        pl: 0.6,
                    }}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                maxHeight: 200,
                            },
                        },
                    }}
                >
                    {options.map((item) => {
                        return (
                            <MenuItem
                                value={item.content}
                                key={item.id}
                                name={inputObj.field}
                                sx={{
                                    fontSize: '13px',
                                }}
                            >
                                {item.content}
                            </MenuItem>
                        );
                    })}
                </Select>
            </FormControl>
        </>
    );
};

/** Component for textfield type input. */
const RequiredInfoTextField = (props) => {
    /** Get props from NewTradePartnerDialog.js and EditTradePartnerDialog.js. */
    const { inputObj, onChange, state, validator } = props;
    return (
        <InputField
            label={inputObj.name}
            name={inputObj.field}
            value={state[inputObj.field] ?? ''}
            onChange={onChange}
            inputStyle={{ fontSize: '13px', width: '100%' }}
            disabled={!inputObj.editable}
            error={inputObj.required ? validator[inputObj.field] : false}
        />
    );
};

/** Component for multiline text fields (address, remark). */
const MultilineTextField = (props) => {
    /** Get props from NewTradePartnerDialog.js and EditTradePartnerDialog.js. */
    const { state, setState, inputObj, validator } = props;
    /** Character limit of the column, matching the DB column length. */
    const maxLength = inputObj.maxLength ?? 200;
    /** Handler for multiline field changes. */
    const handleChange = (e) => {
        const newValue = e.target.value;

        // Update state with the new value only while it stays within the column limit.
        if (newValue.length <= maxLength) {
            setState((prev) => ({
                ...prev,
                [inputObj.field]: newValue,
            }));
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
            <Typography sx={{ color: '#003a52', fontSize: 13, fontWeight: 500 }}>{inputObj.name}</Typography>
            <TextField
                label=""
                value={state[inputObj.field] ?? ''}
                onChange={handleChange}
                multiline
                rows={inputObj.rows ?? 4}
                error={inputObj.required ? validator[inputObj.field] : false}
                slotProps={{
                    input: {
                        maxLength, // limit the input to the DB column length
                    },
                }}
                sx={{
                    fontSize: 12,
                    '& .MuiInputBase-root': {
                        fontSize: '13px',
                        p: 0.5,
                    },
                }}
            />
        </Box>
    );
};

/** Component for multiple selection type input. */
const MultiSelect = (props) => {
    /** Get props from NewTradePartnerDialog.js and EditTradePartnerDialog.js. */
    const { state, setState, inputObj } = props;

    /** Dispatch a function to get ocean carrier data. */
    const dispatch = useDispatch();

    /** State for saving carrier option. */
    const [carrierOptions, setCarrierOptions] = useState([]);

    /** Handler for adding or removing selected carrier. */
    const handleChange = (event, newValue) => {
        const flatNewValue = newValue.flat();
        setState((prev) => ({
            ...prev,
            il_trade_partner_carrier: flatNewValue,
        }));
        return;
    };

    /** Handler to search the ocean carriers. */
    const handleTypingChange = (event) => {
        const value = event.target.value;
        readSocketData(dispatch, '', 'row', 'il_opus_carrier', '', value, (receivedData) => {
            setCarrierOptions(receivedData);
        });
    };

    return (
        <Box
            sx={{
                display: 'flex',
                width: '100%',
                height: 'auto',
                gap: '25px',
                mb: -1,
            }}
        >
            <Typography
                sx={{
                    display: 'inline',
                    color: '#003a52',
                    fontSize: 13,
                    fontWeight: 500,
                    width: '100px',
                }}
            >
                Ocean Carrier
            </Typography>
            <Autocomplete
                name={inputObj.field}
                value={state?.il_trade_partner_carrier || []}
                options={carrierOptions}
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={handleChange}
                disabled={state.partner_type !== 'Terminal'}
                multiple
                disableClearable
                size="small"
                slotProps={{
                    popper: {
                        component: StyledPopper,
                    },
                }}
                sx={{
                    width: '220px',
                    maxHeight: '120px',
                    overflowY: 'auto',
                    '& .MuiInputBase-input': {
                        fontSize: 13,
                        height: 12,
                    },
                }}
                renderInput={(params) => (
                    <TextField {...params} label="" data-name={inputObj.field} onChange={handleTypingChange} />
                )}
                renderOption={(props, option) => {
                    return (
                        <li {...props} key={props.id} data-name={inputObj.field}>
                            {option.code} | {option.name}
                        </li>
                    );
                }}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                            <Chip
                                size="small"
                                variant="outlined"
                                label={option.code}
                                key={key}
                                deleteIcon={<CancelIcon name={inputObj.field} />}
                                {...tagProps}
                            />
                        );
                    })
                }
            />
        </Box>
    );
};

/** Component for rendering different types of trade partner inputs. */
export const PartnerForm = React.memo((props) => {
    /** Get props from NewTradePartnerDialog.js and EditTradePartnerDialog.js. */
    const { inputObj, tradePartnerForm, setTradePartnerForm, purpose, partnerTypeOptions, validator } = props;

    /** Handler for updating the values of the text field and select components in requiredInfoInput. */
    const onChange = (event, newValue) => {
        setTradePartnerForm((prev) => ({
            ...prev,
            [event]: newValue,
        }));
    };

    /** Handler for input changes, except google autocomplete component. */
    const handleInputs = useCallback((e) => {
        onChange(inputObj.field, e.target.value);
    });

    /** Based on inputObj.inputType, use a map object to render components. */
    const inputMap = {
        singleSelect: (
            <RequiredInfoSelectField
                inputObj={inputObj}
                onChange={handleInputs}
                state={tradePartnerForm}
                purpose={purpose}
                partnerTypeOptions={partnerTypeOptions}
                validator={validator}
            />
        ),
        textField: (
            <RequiredInfoTextField
                inputObj={inputObj}
                onChange={handleInputs}
                state={tradePartnerForm}
                validator={validator}
            />
        ),
        textFieldArea: (
            <MultilineTextField
                state={tradePartnerForm}
                setState={setTradePartnerForm}
                inputObj={inputObj}
                validator={validator}
            />
        ),
        multiSelect: <MultiSelect state={tradePartnerForm} setState={setTradePartnerForm} inputObj={inputObj} />,
    };

    return inputMap[inputObj.inputType];
});
